// Complete series configuration with all manuals
export interface Manual {
  slug: string
  title: string
  summary: string
  duration: string
  price?: number
  free?: boolean
  gumroadId: string
  featured?: boolean
  difficulty?: string
  tags?: string[]
  prerequisites?: string[]
  category?: string
  tier?: string
  icon?: string
  includedWith?: string
  /** Book/chapter order within the series (e.g. 1–26 for commodities-energy) */
  seriesOrder?: number
}

export interface Series {
  slug: string
  title: string
  description: string
  icon: string
  color: string
  bundlePrice?: number
  bundleGumroadId?: string
  category: string
  difficulty: string
  manuals: Manual[]
  featured?: boolean
  prerequisites?: string[]
  learningPath?: string[]
}

// 🎯 SMART PRICING CONFIGURATION
export const PRICING_CONFIG = {
  basePrices: {
    beginner: 0.99,
    intermediate: 1.99,
    advanced: 2.49
  },
  bundleDiscountPercent: 50,
  minimumBundlePrice: 4.99,
  currency: '£'
} as const

// 🧮 SMART PRICING FUNCTIONS
export function getManualPrice(manual: Manual): number {
  // Free modules are always £0
  if (manual.free === true || manual.price === 0) return 0
  
  const priceMap: Record<string, number> = {
    beginner: 0,
    intermediate: 0,
    advanced: 0
  }
  
  if (manual.price) return manual.price
  if (manual.difficulty && priceMap[manual.difficulty] !== undefined) {
    return PRICING_CONFIG.basePrices[manual.difficulty as keyof typeof PRICING_CONFIG.basePrices]
  }
  
  return PRICING_CONFIG.basePrices.intermediate
}

export function calculateBundlePrice(manuals: Manual[]): number {
  const totalPrice = manuals.reduce((sum, manual) => sum + getManualPrice(manual), 0)
  const discountedPrice = totalPrice * (1 - PRICING_CONFIG.bundleDiscountPercent / 100)
  return Math.max(discountedPrice, PRICING_CONFIG.minimumBundlePrice)
}

export function calculateBundleSavings(manuals: Manual[]): number {
  const totalPrice = manuals.reduce((sum, manual) => sum + getManualPrice(manual), 0)
  const bundlePrice = calculateBundlePrice(manuals)
  const savings = totalPrice - bundlePrice
  return Math.round(savings * 100) / 100
}
export function formatPrice(price: number): string {
  return `${PRICING_CONFIG.currency}${price.toFixed(2)}`
}

export const seriesConfig: Series[] = [
  {
    slug: "algorithmic-trading",
    title: "Algorithmic Trading",
    description: "",
    icon: "📚",
    color: "blue",
    category: "execution",
    difficulty: "advanced",
    manuals: [
      {
        slug: "execution-algorithms",
        title: "Execution Algorithms & Trade Implementation",
        summary:
          "Master TWAP, VWAP, implementation shortfall, and advanced execution algorithms for optimal trade execution and market impact minimisation",
        duration: "55 min",
        price: 2.49,
        gumroadId: "execution-algorithms-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["algorithms", "execution", "market-impact", "optimisation"],
        prerequisites: [],
        category: "execution",
        tier: "expert",
      },
      {
        slug: "high-frequency-trading",
        title: "High-Frequency Trading: Technology & Strategy",
        summary:
          "Understand HFT strategies, latency optimisation, market making, and the technology infrastructure powering modern electronic trading",
        duration: "65 min",
        price: 2.49,
        gumroadId: "hft-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["hft", "latency", "market-making", "technology"],
        prerequisites: [],
        category: "hft",
        tier: "expert",
      },
      {
        slug: "market-microstructure",
        title: "Market Microstructure",
        summary: "",
        duration: "80 min",
        price: 2.49,
        gumroadId: "market-microstructure",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "algorithmic-trading",
        tier: "professional",
      },
      {
        slug: "systematic-strategies",
        title: "Systematic Strategies",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "systematic-strategies",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "algorithmic-trading",
        tier: "professional",
      },
    ],
  },
  {
    slug: "blockchain-defi",
    title: "Blockchain Defi",
    description: "",
    icon: "📚",
    color: "blue",
    category: "blockchain-defi",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "blockchain-technology",
        title: "Blockchain Technology",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "blockchain-technology",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "blockchain-defi",
        tier: "professional",
      },
      {
        slug: "cryptocurrency-fundamentals",
        title: "Cryptocurrency Fundamentals",
        summary:
          "Essential knowledge of cryptocurrencies, digital wallets, mining, and crypto market dynamics",
        duration: "55 min",
        price: 1.99,
        gumroadId: "crypto-fundamentals-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["cryptocurrency", "bitcoin", "digital-assets", "wallets"],
        prerequisites: [],
        category: "fundamentals",
        tier: "professional",
      },
      {
        slug: "defi-protocols",
        title: "Defi Protocols",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "defi-protocols",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "blockchain-defi",
        tier: "professional",
      },
      {
        slug: "digital-asset-trading",
        title: "Digital Asset Trading",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "digital-asset-trading",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "blockchain-defi",
        tier: "professional",
      },
    ],
  },
  {
    slug: "central-banking",
    title: "Central Banking",
    description: "",
    icon: "📚",
    color: "blue",
    category: "central-banking",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "central-bank-operations",
        title: "Central Bank Operations",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "central-bank-operations",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "central-banking",
        tier: "professional",
      },
      {
        slug: "financial-stability",
        title: "Financial Stability",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "financial-stability",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "central-banking",
        tier: "professional",
      },
      {
        slug: "monetary-policy",
        title: "Monetary Policy",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "monetary-policy",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "central-banking",
        tier: "professional",
      },
      {
        slug: "quantitative-easing",
        title: "Quantitative Easing",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "quantitative-easing",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "central-banking",
        tier: "professional",
      },
    ],
  },
  {
    slug: "collateral-risk",
    title: "Collateral Risk",
    description: "",
    icon: "📚",
    color: "blue",
    category: "collateral-risk",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "collateral-management",
        title: "Collateral Management",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "collateral-management",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "collateral-risk",
        tier: "professional",
      },
      {
        slug: "collateral-optimisation",
        title: "Collateral Optimisation for Derivatives Portfolios",
        summary:
          "A comprehensive professional guide to the principles, regulatory context, and practical implementation of collateral optimisation for derivatives portfolios. Covers eligibility frameworks, constraint modelling, optimisation algorithms, and operational controls with real-world case studies and implementation code.",
        duration: "90–105 minutes",
        price: 1.99,
        gumroadId: "collateral-optimisation-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "collateral",
          "risk",
          "derivatives",
          "treasury",
          "SIMM",
          "optimisation",
          "funding",
          "liquidity",
        ],
        prerequisites: [],
        category: "collateral-risk",
        tier: "professional",
      },
      {
        slug: "collateral-pricing",
        title: "Collateral Pricing",
        summary:
          "Comprehensive guide to collateral valuation methodologies, pricing models, and risk considerations",
        duration: "90 min",
        price: 1.99,
        gumroadId: "collateral-pricing",
        featured: false,
        difficulty: "intermediate",
        tags: ["collateral", "pricing", "valuation", "risk"],
        prerequisites: ["collateral-management"],
        category: "risk",
        tier: "professional",
      },
      {
        slug: "margin-requirements",
        title: "Margin Requirements",
        summary:
          "Comprehensive guide to margin calculation methodologies, regulatory requirements, and risk management",
        duration: "90 min",
        price: 1.99,
        gumroadId: "margin-requirements",
        featured: false,
        difficulty: "intermediate",
        tags: ["margin", "collateral", "risk", "regulation"],
        prerequisites: ["collateral-management", "collateral-pricing"],
        category: "risk",
        tier: "professional",
      },
    ],
  },
  {
    slug: "commodity-markets",
    title: "Commodity Markets",
    description: "",
    icon: "📚",
    color: "blue",
    category: "commodity-markets",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "agricultural-commodities",
        title: "Agricultural Commodities: Grains, Livestock & Softs",
        summary:
          "Complete guide to agricultural commodity trading including grains, livestock, and soft commodities with seasonal patterns and weather risk management",
        duration: "38 min",
        price: 1.99,
        gumroadId: "agricultural-commodities-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "agriculture",
          "grains",
          "livestock",
          "softs",
          "weather",
          "seasonal",
          "commodities",
        ],
        prerequisites: [],
        category: "commodity-markets",
        tier: "professional",
      },
      {
        slug: "commodity-risk-management",
        title: "Commodity Risk Management & Hedging",
        summary:
          "Advanced risk management techniques for commodity portfolios including hedging strategies, basis risk, and operational risk management",
        duration: "42 min",
        price: 1.99,
        gumroadId: "commodity-risk-management-manual",
        featured: false,
        difficulty: "advanced",
        tags: [
          "commodities",
          "risk-management",
          "hedging",
          "basis",
          "VAR",
          "portfolio",
        ],
        prerequisites: [],
        category: "commodity-markets",
        tier: "professional",
      },
      {
        slug: "energy-derivatives",
        title: "Energy Derivatives: Oil, Gas & Power Trading",
        summary:
          "Master energy derivative instruments including crude oil futures, natural gas swaps, and electricity forwards with comprehensive risk management strategies",
        duration: "35 min",
        price: 2.49,
        gumroadId: "energy-derivatives-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["energy", "derivatives", "oil", "gas", "power", "commodities"],
        prerequisites: [],
        category: "commodity-markets",
        tier: "professional",
      },
      {
        slug: "metals-trading",
        title: "Metals Trading: Precious & Industrial Metals",
        summary:
          "Comprehensive guide to precious and industrial metals trading, covering gold, silver, platinum, copper, and aluminum with hedging strategies",
        duration: "32 min",
        price: 1.99,
        gumroadId: "metals-trading-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "metals",
          "gold",
          "silver",
          "copper",
          "aluminum",
          "commodities",
          "hedging",
        ],
        prerequisites: [],
        category: "commodity-markets",
        tier: "professional",
      },
    ],
  },
  {
    slug: "credit-instruments",
    title: "Credit Instruments",
    description: "",
    icon: "📚",
    color: "blue",
    category: "credit",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "corporate-credit-analysis",
        title: "Corporate Credit Analysis",
        summary:
          "Comprehensive guide to analyzing corporate credit risk, financial statements, and credit metrics",
        duration: "90 min",
        price: 1.99,
        gumroadId: "corporate-credit",
        featured: false,
        difficulty: "intermediate",
        tags: ["credit", "analysis", "corporate", "risk"],
        prerequisites: ["credit-default-swaps"],
        category: "credit",
        tier: "professional",
      },
      {
        slug: "credit-default-swaps",
        title: "Credit Default Swaps",
        summary:
          "A comprehensive professional guide to credit default swap mechanics, pricing methodologies, trading strategies, and risk management frameworks. Covers contract fundamentals, market structure, regulatory environment, and advanced portfolio applications with real-world case studies and implementation examples.",
        duration: "85–95 minutes",
        price: 1.99,
        gumroadId: "credit-default-swaps-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "cds",
          "credit-protection",
          "default-risk",
          "credit-derivatives",
          "risk-management",
          "portfolio-hedging",
        ],
        prerequisites: ["bond-mathematics", "derivatives-fundamentals"],
        category: "derivatives",
        tier: "expert",
      },
      {
        slug: "credit-derivatives",
        title: "Credit Derivatives",
        summary:
          "Advanced guide to credit derivatives, including total return swaps, credit-linked notes, and portfolio credit derivatives",
        duration: "90 min",
        price: 1.99,
        gumroadId: "credit-derivatives",
        featured: false,
        difficulty: "advanced",
        tags: ["credit", "derivatives", "structured", "risk"],
        prerequisites: ["credit-default-swaps"],
        category: "credit",
        tier: "professional",
      },
      {
        slug: "trade-finance",
        title: "Trade Finance",
        summary:
          "Comprehensive guide to trade finance instruments, structures, and risk management",
        duration: "90 min",
        price: 2.49,
        gumroadId: "trade-finance",
        featured: false,
        difficulty: "intermediate",
        tags: ["trade", "finance", "credit", "international"],
        prerequisites: ["credit-default-swaps"],
        category: "credit",
        tier: "professional",
      },
      {
        slug: "financial-statement-analysis",
        title: "Financial Statement Analysis",
        summary:
          "Comprehensive guide to analyzing financial statements for credit analysis and investment decisions",
        duration: "45 min",
        price: 1.99,
        gumroadId: "financial-statement-analysis-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["financial-statements", "credit-analysis", "ratio-analysis", "cash-flow", "accounting"],
        prerequisites: [],
        category: "credit",
        tier: "professional",
      },
      {
        slug: "corporate-finance",
        title: "Corporate Finance & Valuation",
        summary:
          "Comprehensive guide to corporate valuation, M&A analysis, and capital structure decisions",
        duration: "48 min",
        price: 1.99,
        gumroadId: "corporate-finance-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["corporate-finance", "valuation", "M&A", "DCF", "capital-structure"],
        prerequisites: ["financial-statement-analysis"],
        category: "credit",
        tier: "professional",
      },
    ],
  },
  {
    slug: "emerging-markets",
    title: "Emerging Markets",
    description: "",
    icon: "📚",
    color: "blue",
    category: "emerging-markets",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "emerging-market-currencies",
        title: "Emerging Market Currencies",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "emerging-market-currencies",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "emerging-markets",
        tier: "professional",
      },
      {
        slug: "emerging-market-debt",
        title: "Emerging Market Debt",
        summary:
          "A comprehensive professional guide to emerging market debt analysis, portfolio management, and investment strategies. Covers sovereign and corporate debt, local and hard currency markets, credit analysis, and risk management with practical examples and institutional insights.",
        duration: "90-100 minutes",
        price: 1.99,
        gumroadId: "emerging-market-debt-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "em-debt",
          "portfolio-management",
          "credit-analysis",
          "risk-assessment",
          "market-structure",
          "institutional-investing",
        ],
        prerequisites: ["bond-mathematics", "government-bonds"],
        category: "debt",
        tier: "professional",
      },
      {
        slug: "emerging-market-equities",
        title: "Emerging Market Equities",
        summary:
          "A comprehensive professional guide to emerging market equity analysis, portfolio management, and investment strategies. Covers market structure, valuation frameworks, risk assessment, and implementation approaches with practical examples and institutional insights.",
        duration: "85-95 minutes",
        price: 1.99,
        gumroadId: "emerging-market-equities-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "em-equities",
          "portfolio-management",
          "valuation",
          "risk-assessment",
          "market-structure",
          "institutional-investing",
        ],
        prerequisites: [
          "equity-futures-trading",
          "equity-options-fundamentals",
        ],
        category: "equities",
        tier: "professional",
      },
      {
        slug: "frontier-markets",
        title: "Frontier Markets",
        summary:
          "Comprehensive analysis of frontier markets, investment opportunities, risks, and market access",
        duration: "90 min",
        price: 1.99,
        gumroadId: "frontier-markets",
        featured: false,
        difficulty: "advanced",
        tags: ["frontier markets", "emerging markets", "investment", "risk"],
        prerequisites: ["emerging-market-equities", "emerging-market-debt"],
        category: "markets",
        tier: "professional",
      },
    ],
  },
  {
    slug: "equity-derivatives",
    title: "Equity Derivatives",
    description: "",
    icon: "📚",
    color: "blue",
    category: "derivatives",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "equity-futures-trading",
        title: "Equity Index Futures",
        summary:
          "Index futures mechanics, arbitrage strategies, and basis trading for equity derivatives",
        duration: "25 min",
        price: 1.99,
        gumroadId: "equity-futures-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["index-futures", "arbitrage", "basis-trading"],
        prerequisites: [],
        category: "derivatives",
        tier: "professional",
      },
      {
        slug: "equity-options-fundamentals",
        title: "Equity Options Fundamentals",
        summary:
          "Options mechanics, pricing models, and basic strategies for equity derivatives",
        duration: "28 min",
        price: 1.99,
        gumroadId: "equity-options-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "equity-options",
          "black-scholes",
          "greeks",
          "options-strategies",
        ],
        prerequisites: [],
        category: "derivatives",
        tier: "professional",
      },
      {
        slug: "equity-swaps",
        title: "Equity Swaps & Total Return Swaps",
        summary:
          "Equity swap mechanics, funding considerations, and institutional applications for synthetic equity exposure",
        duration: "26 min",
        price: 1.99,
        gumroadId: "equity-swaps-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["equity-swaps", "total-return-swaps", "funding"],
        prerequisites: [],
        category: "derivatives",
        tier: "expert",
      },
      {
        slug: "volatility-trading",
        title: "Volatility Trading Strategies",
        summary:
          "Advanced volatility trading, VIX products, and dispersion trading strategies for equity markets",
        duration: "32 min",
        price: 2.49,
        gumroadId: "volatility-trading-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["volatility", "vix", "dispersion-trading"],
        prerequisites: [],
        category: "trading",
        tier: "expert",
      },
    ],
  },
  {
    slug: "fintech-innovation",
    title: "Fintech Innovation",
    description: "",
    icon: "📚",
    color: "blue",
    category: "technology",
    difficulty: "advanced",
    manuals: [
      {
        slug: "api-integration",
        title: "API Integration & Data Analytics",
        summary: "Financial APIs, data integration, and analytics platforms",
        duration: "30 min",
        price: 2.49,
        gumroadId: "api-integration",
        featured: false,
        difficulty: "advanced",
        tags: ["apis", "data-integration", "analytics"],
        prerequisites: [],
        category: "technology",
        tier: "expert",
      },
      {
        slug: "digital-payments",
        title: "Digital Payments & Banking",
        summary:
          "Payment systems, digital wallets, and open banking frameworks",
        duration: "26 min",
        price: 2.49,
        gumroadId: "digital-payments-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["digital-payments", "wallets", "open-banking"],
        prerequisites: [],
        category: "payments",
        tier: "professional",
      },
      {
        slug: "regtech-solutions",
        title: "RegTech & Compliance Technology",
        summary:
          "Regulatory technology, automated compliance, and risk monitoring",
        duration: "28 min",
        price: 1.99,
        gumroadId: "regtech-solutions",
        featured: false,
        difficulty: "intermediate",
        tags: ["regtech", "compliance-tech", "risk-monitoring"],
        prerequisites: [],
        category: "compliance",
        tier: "professional",
      },
      {
        slug: "robo-advisors",
        title: "Robo-Advisors & Automated Investing",
        summary:
          "Algorithmic portfolio management, robo-advisory platforms, and automation",
        duration: "24 min",
        price: 1.99,
        gumroadId: "robo-advisors",
        featured: false,
        difficulty: "intermediate",
        tags: ["robo-advisors", "automated-investing", "algorithms"],
        prerequisites: [],
        category: "automation",
        tier: "professional",
      },
      {
        slug: "open-banking",
        title: "Open Banking & API Economy",
        summary:
          "Comprehensive guide to open banking frameworks, API standards, and financial services innovation",
        duration: "35 min",
        price: 1.99,
        gumroadId: "open-banking-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["open-banking", "APIs", "PSD2", "fintech", "banking-as-a-service"],
        prerequisites: ["digital-payments"],
        category: "technology",
        tier: "professional",
      },
    ],
  },
  {
    slug: "fixed-income-debt",
    title: "Fixed Income Debt",
    description: "",
    icon: "📚",
    color: "blue",
    category: "fixed-income-debt",
    difficulty: "beginner",
    manuals: [
      {
        slug: "corporate-bonds",
        title: "Corporate Bond Analysis",
        summary:
          "Investment grade and high yield corporate debt markets, credit spreads, and rating methodologies",
        duration: "26 min",
        price: 0.99,
        gumroadId: "corporate-bonds-manual",
        featured: false,
        difficulty: "beginner",
        tags: [
          "corporate-bonds",
          "credit-risk",
          "rating-agencies",
          "spread-analysis",
        ],
        prerequisites: [],
        category: "fixed-income-debt",
        tier: "professional",
      },
      {
        slug: "em-debt-markets",
        title: "Emerging Market Debt",
        summary:
          "A comprehensive professional guide to emerging market debt instruments, sovereign and corporate credit analysis, currency considerations, and investment strategies. Covers market structure, risk assessment frameworks, portfolio construction, and ESG integration with real-world case studies and implementation examples.",
        duration: "90–100 minutes",
        price: 0.99,
        gumroadId: "emerging-market-debt-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "em-debt",
          "sovereign",
          "local-currency",
          "hard-currency",
          "political-risk",
          "credit-analysis",
          "portfolio-construction",
          "currency-hedging",
        ],
        prerequisites: ["bond-mathematics", "government-bonds"],
        category: "debt",
        tier: "professional",
      },
      {
        slug: "government-bonds",
        title: "Government Bond Fundamentals",
        summary:
          "Essential foundation for understanding sovereign debt markets, yield curves, and government securities",
        duration: "22 min",
        price: 1.99,
        gumroadId: "government-bonds-manual",
        featured: false,
        difficulty: "beginner",
        tags: ["government-bonds", "yield-curve", "treasury", "sovereign-debt"],
        prerequisites: [],
        category: "fixed-income-debt",
        tier: "professional",
      },
      {
        slug: "municipal-bonds",
        title: "Municipal Bonds",
        summary:
          "Tax-exempt municipal securities, credit analysis, and investment strategies",
        duration: "24 min",
        price: 0.99,
        gumroadId: "municipal-bonds-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "municipal-bonds",
          "tax-exempt",
          "credit-analysis",
          "public-finance",
        ],
        prerequisites: ["government-bonds"],
        category: "bonds",
        tier: "professional",
      },
      {
        slug: "sovereign-debt",
        title: "Sovereign Debt Analysis",
        summary:
          "Sovereign bond markets, country risk assessment, and emerging market debt",
        duration: "29 min",
        price: 1.99,
        gumroadId: "sovereign-debt-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "sovereign-debt",
          "country-risk",
          "emerging-markets",
          "government-bonds",
        ],
        prerequisites: ["government-bonds"],
        category: "bonds",
        tier: "professional",
      },
    ],
  },
  {
    slug: "fx-instruments",
    title: "Fx Instruments",
    description: "",
    icon: "📚",
    color: "blue",
    category: "fx-instruments",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "ccswaps",
        title: "Ccswaps",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "ccswaps",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "fx-instruments",
        tier: "professional",
      },
      {
        slug: "ccswaps_TEST_ENHANCED",
        title: "Ccswaps_TEST_ENHANCED",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "ccswaps_TEST_ENHANCED",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "fx-instruments",
        tier: "professional",
      },
      {
        slug: "em-fx-markets",
        title: "Emerging Market FX",
        summary:
          "A comprehensive professional guide to emerging market currency dynamics, trading strategies, intervention policies, and risk management frameworks. Covers market structure, fundamental analysis, carry trades, crisis management, and regional currency characteristics with practical implementation examples.",
        duration: "75–85 minutes",
        price: 1.99,
        gumroadId: "emerging-market-fx-professional",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "em-fx",
          "carry-trades",
          "intervention",
          "currency-dynamics",
          "political-risk",
          "central-banking",
          "portfolio-management",
        ],
        prerequisites: ["spot-fx-trading", "fx-forward-contracts"],
        category: "fx",
        tier: "professional",
      },
      {
        slug: "fx-forward-contracts",
        title: "FX Forward Contracts",
        summary:
          "Deep dive into FX forwards - pricing models, hedge accounting, settlement mechanics, and strategic applications for corporate treasury and risk management",
        duration: "40 min",
        price: 1.99,
        gumroadId: "fx-forwards-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "forwards",
          "hedging",
          "pricing",
          "settlement",
          "corporate-treasury",
        ],
        prerequisites: ["introduction-to-fx", "spot-fx-trading"],
        category: "hedging",
        tier: "professional",
      },
      {
        slug: "fx-options",
        title: "FX Options: Advanced Risk Management",
        summary:
          "Master FX options from vanilla strategies to exotic structures. Covers volatility trading, option pricing models, and sophisticated hedging techniques",
        duration: "50 min",
        price: 1.99,
        gumroadId: "fx-options-manual",
        featured: false,
        difficulty: "advanced",
        tags: [
          "options",
          "volatility",
          "hedging",
          "exotic",
          "structured-products",
        ],
        prerequisites: [
          "introduction-to-fx",
          "spot-fx-trading",
          "fx-forward-contracts",
        ],
        category: "options",
        tier: "expert",
      },
      {
        slug: "fx-swaps",
        title: "FX Swaps: Funding and Liquidity Tools",
        summary:
          "Understand FX swaps mechanics, funding applications, central bank operations, and their critical role in global liquidity management",
        duration: "38 min",
        price: 2.49,
        gumroadId: "fx-swaps-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["swaps", "funding", "liquidity", "central-banks", "operations"],
        prerequisites: ["introduction-to-fx", "spot-fx-trading"],
        category: "funding",
        tier: "professional",
      },
      {
        slug: "introduction-to-fx",
        title: "Introduction to Foreign Exchange (FX): A Professional Guide",
        summary:
          "Master the fundamentals of global FX markets including market structure, currency analysis, risk management, and quantitative techniques with practical Python implementations",
        duration: "90 min",
        price: 1.99,
        gumroadId: "fx-fundamentals-pro",
        featured: false,
        difficulty: "beginner",
        tags: [
          "fx-basics",
          "market-overview",
          "fundamentals",
          "currency",
          "quotes",
          "market-structure",
          "risk-management",
          "python-implementation",
        ],
        prerequisites: [],
        category: "fundamentals",
        tier: "professional",
      },
      {
        slug: "spot-fx-trading",
        title: "Spot FX Trading Fundamentals",
        summary:
          "Master spot foreign exchange markets with comprehensive insights on market structure, pricing mechanisms, execution strategies, and risk management",
        duration: "90 min",
        price: 0.99,
        gumroadId: "spot-fx-manual",
        featured: true,
        difficulty: "beginner",
        tags: ["spot", "execution", "pricing", "settlement", "fx-trading"],
        prerequisites: ["introduction-to-fx"],
        category: "trading",
        tier: "foundation",
      },
    ],
  },
  {
    slug: "fx-markets",
    title: "Fx Markets",
    description: "",
    icon: "📚",
    color: "blue",
    category: "fx",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "fx-market-participants",
        title: "FX Market Participants",
        summary:
          "Comprehensive analysis of FX market participants, their roles, strategies, and impact on market dynamics",
        duration: "90 min",
        price: 1.99,
        gumroadId: "fx-market-participants",
        featured: false,
        difficulty: "intermediate",
        tags: ["fx", "market participants", "trading", "strategy"],
        prerequisites: ["fx-market-structure"],
        category: "fx",
        tier: "professional",
      },
      {
        slug: "fx-market-structure",
        title: "FX Market Structure",
        summary:
          "Comprehensive guide to global FX market structure, trading venues, and market participants",
        duration: "90 min",
        price: 1.99,
        gumroadId: "fx-market-structure",
        featured: false,
        difficulty: "intermediate",
        tags: ["fx", "market structure", "trading", "venues"],
        prerequisites: ["introduction-to-fx"],
        category: "fx",
        tier: "professional",
      },
      {
        slug: "global-fx-centers",
        title: "Global FX Centers",
        summary:
          "Comprehensive analysis of major global FX trading centers, their characteristics, and roles in the international market",
        duration: "90 min",
        price: 1.99,
        gumroadId: "global-fx-centers",
        featured: false,
        difficulty: "intermediate",
        tags: ["fx", "trading centers", "global markets", "liquidity"],
        prerequisites: ["fx-market-structure"],
        category: "fx",
        tier: "professional",
      },
    ],
  },
  {
    slug: "interest-rates",
    title: "Interest Rates",
    description: "",
    icon: "📚",
    color: "blue",
    category: "analytics",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "bond-mathematics",
        title: "Bond Mathematics",
        summary:
          "Master bond pricing, yield calculations, duration, and convexity for fixed income analysis",
        duration: "26 min",
        price: 1.99,
        gumroadId: "bond-mathematics-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["bond-pricing", "duration", "convexity", "yield-calculations"],
        prerequisites: [],
        category: "analytics",
        tier: "professional",
      },
      {
        slug: "curve-trading-strategies",
        title: "Curve Trading Strategies",
        summary:
          "Advanced yield curve positioning, steepeners, flatteners, and butterfly trades",
        duration: "32 min",
        price: 1.99,
        gumroadId: "curve-trading-strategies-manual",
        featured: false,
        difficulty: "advanced",
        tags: [
          "yield-curve",
          "steepener",
          "flattener",
          "butterfly",
          "curve-trading",
        ],
        prerequisites: ["yield-curves-fundamentals", "bond-mathematics"],
        category: "trading",
        tier: "expert",
      },
      {
        slug: "fras",
        title: "Forward Rate Agreements",
        summary:
          "FRA mechanics, pricing formulas, and practical trading applications",
        duration: "22 min",
        price: 2.49,
        gumroadId: "fras-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["forwards", "rates", "derivatives"],
        prerequisites: [],
        category: "interest-rates",
        tier: "professional",
      },
      {
        slug: "interest-rate-swaps",
        title: "Interest Rate Swaps",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "interest-rate-swaps",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "interest-rates",
        tier: "professional",
      },
      {
        slug: "swaptions-caps-floors",
        title: "Swaptions, Caps & Floors",
        summary:
          "Interest rate derivatives for hedging and speculation - swaptions, caps, and floors",
        duration: "27 min",
        price: 1.99,
        gumroadId: "swaptions-caps-floors-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["swaptions", "caps", "floors", "interest-rate-derivatives"],
        prerequisites: ["yield-curves-fundamentals", "interest-rate-swaps"],
        category: "derivatives",
        tier: "expert",
      },
      {
        slug: "yield-curves-fundamentals",
        title: "Yield Curves Fundamentals",
        summary:
          "Master yield curve construction, analysis, and interpretation for fixed income markets",
        duration: "24 min",
        price: 2.49,
        gumroadId: "yield-curves-fundamentals-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "yield-curves",
          "term-structure",
          "fixed-income",
          "interest-rates",
        ],
        prerequisites: [],
        category: "analytics",
        tier: "professional",
      },
    ],
  },
  {
    slug: "islamic-finance",
    title: "Islamic Finance",
    description: "",
    icon: "📚",
    color: "blue",
    category: "banking",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "islamic-banking",
        title: "Islamic Banking Operations",
        summary: "Murabaha, ijara, musharaka, and Islamic banking products",
        duration: "30 min",
        price: 1.99,
        gumroadId: "islamic-banking",
        featured: false,
        difficulty: "intermediate",
        tags: ["islamic-banking", "murabaha", "ijara", "musharaka"],
        prerequisites: [],
        category: "banking",
        tier: "professional",
      },
      {
        slug: "sharia-principles",
        title: "Sharia Principles in Finance",
        summary:
          "Islamic law foundations, prohibited elements, and compliance frameworks",
        duration: "25 min",
        price: 1.99,
        gumroadId: "sharia-principles-manual",
        featured: false,
        difficulty: "beginner",
        tags: ["sharia", "islamic-law", "compliance", "principles"],
        prerequisites: [],
        category: "fundamentals",
        tier: "foundation",
      },
      {
        slug: "sukuk-markets",
        title: "Sukuk Markets & Structures",
        summary: "Islamic bonds, sukuk structures, and pricing mechanisms",
        duration: "28 min",
        price: 0.99,
        gumroadId: "sukuk-markets",
        featured: false,
        difficulty: "intermediate",
        tags: ["sukuk", "islamic-bonds", "structures", "pricing"],
        prerequisites: [],
        category: "bonds",
        tier: "professional",
      },
      {
        slug: "takaful-insurance",
        title: "Takaful & Islamic Insurance",
        summary:
          "Islamic insurance principles, takaful structures, and risk management",
        duration: "22 min",
        price: 1.99,
        gumroadId: "takaful-insurance",
        featured: false,
        difficulty: "intermediate",
        tags: ["takaful", "islamic-insurance", "risk-management"],
        prerequisites: [],
        category: "insurance",
        tier: "professional",
      },
    ],
  },
  {
    slug: "liquidity-funding",
    title: "Liquidity Funding",
    description: "",
    icon: "📚",
    color: "blue",
    category: "operations",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "cash-flow-management",
        title: "Cash Flow Management",
        summary:
          "Advanced cash flow forecasting, optimization, and working capital management strategies",
        duration: "25 min",
        price: 1.99,
        gumroadId: "cash-flow-management-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["cash-flow", "forecasting", "working-capital", "treasury"],
        prerequisites: [],
        category: "operations",
        tier: "professional",
      },
      {
        slug: "funding-optimization",
        title: "Liquidity Funding Management",
        summary:
          "Strategic liquidity management, funding optimization, and regulatory compliance frameworks",
        duration: "28 min",
        price: 1.99,
        gumroadId: "liquidity-funding-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["liquidity", "funding", "alm", "regulatory"],
        prerequisites: [],
        category: "risk",
        tier: "professional",
      },
      {
        slug: "intraday-liquidity",
        title: "Intraday Liquidity",
        summary:
          "Comprehensive guide to intraday liquidity management, monitoring, and risk control",
        duration: "90 min",
        price: 1.99,
        gumroadId: "intraday-liquidity",
        featured: false,
        difficulty: "intermediate",
        tags: ["liquidity", "intraday", "risk", "treasury"],
        prerequisites: ["liquidity-management"],
        category: "liquidity",
        tier: "professional",
      },
      {
        slug: "liquidity-management",
        title: "Liquidity Risk Management",
        summary:
          "Funding liquidity, market liquidity, and regulatory requirements",
        duration: "25 min",
        price: 1.99,
        gumroadId: "liquidity-risk-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["liquidity-risk", "funding", "lcr", "nsfr"],
        prerequisites: [],
        category: "risk",
        tier: "professional",
      },
    ],
  },
  {
    slug: "money-markets",
    title: "Money Markets",
    description: "",
    icon: "📚",
    color: "blue",
    category: "money-markets",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "call-accounts",
        title: "Call Accounts",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "call-accounts",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "money-markets",
        tier: "professional",
      },
      {
        slug: "certificates-deposit",
        title: "Certificates Deposit",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "certificates-deposit",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "money-markets",
        tier: "professional",
      },
      {
        slug: "commercial-paper",
        title: "Commercial Paper",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "commercial-paper",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "money-markets",
        tier: "professional",
      },
      {
        slug: "repos-reverse-repos",
        title: "Repos Reverse Repos",
        summary: "",
        duration: "80 min",
        price: 1.99,
        gumroadId: "repos-reverse-repos",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "money-markets",
        tier: "professional",
      },
      {
        slug: "securities-lending",
        title: "Securities Lending & Repo Markets",
        summary:
          "Comprehensive guide to securities lending, repo transactions, and financing market mechanics",
        duration: "38 min",
        price: 2.49,
        gumroadId: "securities-lending-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["securities-lending", "repo", "financing", "collateral", "prime-brokerage"],
        prerequisites: ["repos-reverse-repos"],
        category: "money-markets",
        tier: "expert",
      },
    ],
  },
  {
    slug: "portfolio-management",
    title: "Portfolio Management",
    description: "",
    icon: "📚",
    color: "blue",
    category: "strategy",
    difficulty: "advanced",
    manuals: [
      {
        slug: "alternative-investments",
        title: "Alternative Investments",
        summary:
          "Hedge funds, private equity, real estate, and infrastructure investing strategies and analysis",
        duration: "35 min",
        price: 2.49,
        gumroadId: "alternatives-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["alternatives", "hedge-funds", "private-equity", "real-estate"],
        prerequisites: [],
        category: "strategy",
        tier: "expert",
      },
      {
        slug: "factor-investing",
        title: "Factor Investing & Smart Beta Strategies",
        summary:
          "Comprehensive guide to factor models, smart beta implementation, and multi-factor portfolio construction for institutional investors",
        duration: "85 min",
        price: 2.49,
        gumroadId: "factor-investing-manual",
        featured: false,
        difficulty: "advanced",
        tags: [
          "factor-investing",
          "smart-beta",
          "multi-factor",
          "fama-french",
          "risk-premia",
          "systematic-strategies",
        ],
        prerequisites: [],
        category: "portfolio-management",
        tier: "professional",
      },
      {
        slug: "modern-portfolio-theory",
        title: "Modern Portfolio Theory & Optimization",
        summary:
          "Comprehensive guide to Markowitz portfolio theory, efficient frontier construction, and practical portfolio optimization techniques for financial professionals",
        duration: "90 min",
        price: 2.49,
        gumroadId: "modern-portfolio-theory-manual",
        featured: false,
        difficulty: "advanced",
        tags: [
          "portfolio",
          "optimization",
          "markowitz",
          "efficient-frontier",
          "risk",
          "return",
          "capm",
          "diversification",
        ],
        prerequisites: [],
        category: "portfolio-management",
        tier: "professional",
      },
      {
        slug: "performance-attribution",
        title: "Performance Attribution & Measurement",
        summary:
          "Performance analysis, attribution models, and risk-adjusted return calculations for institutional portfolios",
        duration: "27 min",
        price: 2.49,
        gumroadId: "performance-attribution-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["performance", "attribution", "risk-adjusted-returns"],
        prerequisites: [],
        category: "analytics",
        tier: "professional",
      },
      {
        slug: "esg-investing",
        title: "ESG & Sustainable Investing",
        summary:
          "Comprehensive guide to environmental, social, and governance investing strategies and integration",
        duration: "40 min",
        price: 1.99,
        gumroadId: "esg-investing-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["ESG", "sustainable-investing", "green-bonds", "impact-investing", "carbon-markets"],
        prerequisites: ["modern-portfolio-theory"],
        category: "portfolio-management",
        tier: "professional",
      },
      {
        slug: "private-equity",
        title: "Private Equity & Venture Capital",
        summary:
          "Comprehensive guide to private equity investing, fund structures, and value creation strategies",
        duration: "45 min",
        price: 2.49,
        gumroadId: "private-equity-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["private-equity", "venture-capital", "leveraged-buyout", "fund-structure", "due-diligence"],
        prerequisites: ["alternative-investments"],
        category: "portfolio-management",
        tier: "expert",
      },
      {
        slug: "real-estate-investment",
        title: "Real Estate Investment",
        summary:
          "Comprehensive guide to real estate investment trusts, property derivatives, and real estate portfolio management",
        duration: "42 min",
        price: 2.49,
        gumroadId: "real-estate-investment-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["real-estate", "REITs", "property", "commercial-real-estate", "property-derivatives"],
        prerequisites: ["alternative-investments"],
        category: "portfolio-management",
        tier: "expert",
      },
      {
        slug: "wealth-management",
        title: "Wealth Management & Family Offices",
        summary:
          "Comprehensive guide to high net worth wealth management, family office operations, and multi-generational planning",
        duration: "42 min",
        price: 2.49,
        gumroadId: "wealth-management-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["wealth-management", "family-office", "estate-planning", "UHNW", "trust-structures"],
        prerequisites: ["modern-portfolio-theory", "alternative-investments"],
        category: "portfolio-management",
        tier: "expert",
      },
    ],
  },
  {
    slug: "quantitative-methods",
    title: "Quantitative Methods",
    description: "",
    icon: "📚",
    color: "blue",
    category: "technology",
    difficulty: "advanced",
    manuals: [
      {
        slug: "machine-learning-finance",
        title: "Machine Learning in Finance",
        summary:
          "Comprehensive guide to implementing machine learning in institutional finance, covering advanced trading strategies, risk management, and portfolio optimisation with real-world applications from leading financial institutions",
        duration: "85 min",
        price: 2.49,
        gumroadId: "ml-finance-manual",
        featured: true,
        difficulty: "advanced",
        tags: [
          "machine-learning",
          "ai",
          "algorithmic-trading",
          "risk-management",
          "portfolio-optimisation",
          "quantitative-finance",
        ],
        prerequisites: [
          "quantitative-methods/stochastic-calculus",
          "quantitative-methods/numerical-methods",
        ],
        category: "technology",
        tier: "expert",
      },
      {
        slug: "monte-carlo-methods",
        title: "Monte Carlo Methods",
        summary:
          "Simulation techniques, variance reduction methods, and pricing applications in financial modeling",
        duration: "38 min",
        price: 2.49,
        gumroadId: "monte-carlo-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["monte-carlo", "simulation", "variance-reduction"],
        prerequisites: [],
        category: "mathematics",
        tier: "expert",
      },
      {
        slug: "numerical-methods",
        title: "Numerical Methods in Finance",
        summary:
          "Finite difference methods, tree models, and PDE solutions for derivatives pricing and risk management",
        duration: "42 min",
        price: 2.49,
        gumroadId: "numerical-methods-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["numerical-methods", "finite-difference", "pde"],
        prerequisites: [],
        category: "mathematics",
        tier: "expert",
      },
      {
        slug: "stochastic-calculus",
        title: "Stochastic Calculus for Finance",
        summary:
          "Brownian motion, Ito calculus, and stochastic differential equations",
        duration: "45 min",
        price: 2.49,
        gumroadId: "stochastic-calculus-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["stochastic-calculus", "brownian-motion", "ito-calculus", "sde"],
        prerequisites: [],
        category: "mathematics",
        tier: "expert",
      },
    ],
  },
  {
    slug: "regulation-compliance",
    title: "Regulation Compliance",
    description: "",
    icon: "📚",
    color: "blue",
    category: "banking-regulation",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "basel-framework",
        title: "Basel III/IV Framework",
        summary:
          "Capital requirements, liquidity ratios, and risk-weighted assets under the Basel regulatory framework",
        duration: "35 min",
        price: 1.99,
        gumroadId: "basel-framework-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["basel", "capital-requirements", "liquidity", "rwa"],
        prerequisites: [],
        category: "banking-regulation",
        tier: "professional",
      },
      {
        slug: "conduct-market-abuse",
        title: "Conduct & Market Abuse",
        summary:
          "Comprehensive guide to market conduct regulations, market abuse prevention, and compliance frameworks",
        duration: "90 min",
        price: 1.99,
        gumroadId: "conduct-market-abuse",
        featured: false,
        difficulty: "intermediate",
        tags: ["regulation", "compliance", "conduct", "market abuse"],
        prerequisites: ["mifid-dodd-frank"],
        category: "regulation",
        tier: "professional",
      },
      {
        slug: "emir-reporting",
        title: "EMIR & Reporting Requirements",
        summary:
          "Comprehensive guide to EMIR regulations, transaction reporting requirements, and compliance frameworks",
        duration: "90 min",
        price: 1.99,
        gumroadId: "emir-reporting",
        featured: false,
        difficulty: "intermediate",
        tags: ["regulation", "compliance", "reporting", "EMIR"],
        prerequisites: ["basel-framework"],
        category: "regulation",
        tier: "professional",
      },
      {
        slug: "kyc-aml-compliance",
        title: "KYC & AML Compliance",
        summary:
          "Comprehensive guide to Know Your Customer (KYC) procedures and Anti-Money Laundering (AML) compliance",
        duration: "90 min",
        price: 1.99,
        gumroadId: "kyc-aml",
        featured: false,
        difficulty: "intermediate",
        tags: ["regulation", "compliance", "KYC", "AML"],
        prerequisites: ["mifid-dodd-frank"],
        category: "regulation",
        tier: "professional",
      },
      {
        slug: "mifid-dodd-frank",
        title: "MiFID II & Dodd-Frank",
        summary:
          "Trading regulations, transparency requirements, and derivatives markets under MiFID II and Dodd-Frank",
        duration: "32 min",
        price: 1.99,
        gumroadId: "mifid-dodd-frank-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["mifid", "dodd-frank", "transparency", "derivatives-regulation"],
        prerequisites: [],
        category: "trading-regulation",
        tier: "professional",
      },
    ],
  },
  {
    slug: "risk-management",
    title: "Risk Management",
    description: "",
    icon: "📚",
    color: "blue",
    category: "risk",
    difficulty: "advanced",
    manuals: [
      {
        slug: "collateral-management",
        title: "Collateral Management Systems",
        summary:
          "End-to-end collateral lifecycle, optimization, and operational frameworks",
        duration: "32 min",
        price: 2.49,
        gumroadId: "collateral-management-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["collateral", "margining", "operations"],
        prerequisites: [],
        category: "risk",
        tier: "expert",
      },
      {
        slug: "credit-risk-modeling",
        title: "Credit Risk Modeling",
        summary: "PD, LGD, EAD modeling and credit portfolio risk management",
        duration: "30 min",
        price: 2.49,
        gumroadId: "credit-risk-modeling-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["credit-modeling", "pd", "lgd", "ead", "portfolio-risk"],
        prerequisites: ["market-risk-fundamentals"],
        category: "risk",
        tier: "expert",
      },
      {
        slug: "liquidity-risk",
        title: "Liquidity Risk Management",
        summary:
          "Funding liquidity, market liquidity, and regulatory requirements",
        duration: "25 min",
        price: 2.49,
        gumroadId: "liquidity-risk-manual",
        featured: false,
        difficulty: "intermediate",
        tags: ["liquidity-risk", "funding", "lcr", "nsfr"],
        prerequisites: [],
        category: "risk",
        tier: "professional",
      },
      {
        slug: "market-risk-fundamentals",
        title: "Market Risk Fundamentals",
        summary:
          "Comprehensive guide to market risk measurement, VaR modeling, Expected Shortfall, and institutional risk management frameworks",
        duration: "80 min",
        price: 1.99,
        gumroadId: "market-risk-fundamentals-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "market-risk",
          "var",
          "expected-shortfall",
          "risk-modeling",
          "basel-framework",
          "stress-testing",
        ],
        prerequisites: [],
        category: "risk-management",
        tier: "professional",
      },
      {
        slug: "operational-risk",
        title: "Operational Risk Management",
        summary:
          "Comprehensive guide to operational risk frameworks, Basel III requirements, loss modeling, and institutional risk management practices",
        duration: "75 min",
        price: 1.99,
        gumroadId: "operational-risk-manual",
        featured: false,
        difficulty: "intermediate",
        tags: [
          "operational-risk",
          "basel-iii",
          "loss-modeling",
          "risk-frameworks",
          "business-continuity",
          "cyber-risk",
        ],
        prerequisites: [],
        category: "risk-management",
        tier: "professional",
      },
      {
        slug: "xva-framework",
        title: "XVA Framework & Counterparty Risk",
        summary:
          "Comprehensive guide to CVA, DVA, FVA, and XVA management for derivatives portfolios",
        duration: "50 min",
        price: 2.49,
        gumroadId: "xva-framework-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["XVA", "CVA", "DVA", "FVA", "counterparty-risk", "derivatives", "pricing"],
        prerequisites: ["credit-risk-modeling", "interest-rate-swaps"],
        category: "risk-management",
        tier: "expert",
      },
      {
        slug: "stress-testing",
        title: "Stress Testing & Scenario Analysis",
        summary:
          "Comprehensive guide to regulatory stress testing, scenario design, and capital planning",
        duration: "45 min",
        price: 2.49,
        gumroadId: "stress-testing-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["stress-testing", "CCAR", "DFAST", "scenario-analysis", "capital-planning"],
        prerequisites: ["market-risk-fundamentals", "credit-risk-modeling"],
        category: "risk-management",
        tier: "expert",
      },
      {
        slug: "model-validation",
        title: "Model Validation & Model Risk",
        summary:
          "Comprehensive guide to model risk management, validation frameworks, and governance",
        duration: "40 min",
        price: 2.49,
        gumroadId: "model-validation-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["model-risk", "validation", "governance", "SR-11-7", "model-lifecycle"],
        prerequisites: ["market-risk-fundamentals", "credit-risk-modeling"],
        category: "risk-management",
        tier: "expert",
      },
    ],
  },
  {
    slug: "structured-products",
    title: "Structured Products",
    description: "",
    icon: "📚",
    color: "blue",
    category: "hybrid-securities",
    difficulty: "advanced",
    manuals: [
      {
        slug: "convertible-bonds",
        title: "Convertible Bonds",
        summary:
          "Convertible bond analysis, pricing models, and hedging strategies for hybrid securities",
        duration: "28 min",
        price: 2.49,
        gumroadId: "convertibles-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["convertibles", "hybrid-securities", "delta-hedging"],
        prerequisites: [],
        category: "hybrid-securities",
        tier: "expert",
      },
      {
        slug: "exotic-derivatives",
        title: "Exotic Derivatives",
        summary:
          "Path-dependent options, multi-asset derivatives, and complex payoffs",
        duration: "35 min",
        price: 2.49,
        gumroadId: "exotic-derivatives",
        featured: false,
        difficulty: "advanced",
        tags: ["exotic-derivatives", "path-dependent", "multi-asset"],
        prerequisites: [],
        category: "derivatives",
        tier: "expert",
      },
      {
        slug: "hybrid-instruments",
        title: "Hybrid Instruments",
        summary:
          "Contingent convertibles, preferred shares, and regulatory capital instruments",
        duration: "26 min",
        price: 2.49,
        gumroadId: "hybrid-instruments",
        featured: false,
        difficulty: "advanced",
        tags: ["hybrids", "cocos", "preferred-shares", "regulatory-capital"],
        prerequisites: [],
        category: "regulatory",
        tier: "expert",
      },
      {
        slug: "structured-notes",
        title: "Structured Notes & Capital Protection",
        summary:
          "Principal-protected notes, barrier structures, and payoff design for structured investment products",
        duration: "30 min",
        price: 2.49,
        gumroadId: "structured-notes-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["structured-notes", "capital-protection", "barriers"],
        prerequisites: [],
        category: "structured-products",
        tier: "expert",
      },
    ],
  },
  {
    slug: "treasury-systems",
    title: "Treasury Systems",
    description: "",
    icon: "📚",
    color: "blue",
    category: "treasury",
    difficulty: "intermediate",
    manuals: [
      {
        slug: "payment-systems",
        title: "Payment Systems",
        summary:
          "Comprehensive guide to payment systems architecture, infrastructure, and operations in treasury management",
        duration: "90 min",
        price: 1.99,
        gumroadId: "payment-systems",
        featured: false,
        difficulty: "intermediate",
        tags: ["treasury", "payments", "systems", "infrastructure"],
        prerequisites: ["treasury-technology"],
        category: "treasury",
        tier: "professional",
      },
      {
        slug: "risk-systems",
        title: "Risk Management Systems",
        summary:
          "Real-time risk systems, position management, and automated controls for financial institutions",
        duration: "33 min",
        price: 1.99,
        gumroadId: "risk-systems-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["risk-systems", "real-time", "controls"],
        prerequisites: [],
        category: "technology",
        tier: "expert",
      },
      {
        slug: "settlement-systems",
        title: "Settlement Systems",
        summary:
          "Comprehensive guide to settlement systems, processes, and risk management in treasury operations",
        duration: "90 min",
        price: 2.49,
        gumroadId: "settlement-systems",
        featured: false,
        difficulty: "intermediate",
        tags: ["treasury", "settlement", "systems", "risk"],
        prerequisites: ["treasury-technology", "payment-systems"],
        category: "treasury",
        tier: "professional",
      },
      {
        slug: "trading-systems",
        title: "Trading Systems & Platforms",
        summary:
          "Electronic trading platforms, order management, and execution systems",
        duration: "35 min",
        price: 1.99,
        gumroadId: "trading-systems-manual",
        featured: false,
        difficulty: "advanced",
        tags: ["trading-systems", "oms", "execution", "electronic-trading"],
        prerequisites: ["treasury-technology"],
        category: "technology",
        tier: "expert",
      },
      {
        slug: "treasury-technology",
        title: "Treasury Technology",
        summary: "",
        duration: "80 min",
        price: 2.49,
        gumroadId: "treasury-technology",
        featured: false,
        difficulty: "intermediate",
        tags: [],
        prerequisites: [],
        category: "treasury-systems",
        tier: "professional",
      },
    ],
  },
];

// Tailwind-safe color classes for series theming
export type SeriesColor = 'blue' | 'green' | 'cyan' | 'red' | 'purple' | 'orange';

type ColorClasses = {
  bg: string;
  border: string;
  text: string;
  accent: string;
  button: string;
};

const COLOR_MAP: Record<SeriesColor, ColorClasses> = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-900',
    text: 'text-blue-700 dark:text-blue-300',
    accent: 'text-blue-600 dark:text-blue-400',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-950/30',
    border: 'border-green-200 dark:border-green-900',
    text: 'text-green-700 dark:text-green-300',
    accent: 'text-green-600 dark:text-green-400',
    button: 'bg-green-600 hover:bg-green-700 text-white',
  },
  cyan: {
    bg: 'bg-cyan-50 dark:bg-cyan-950/30',
    border: 'border-cyan-200 dark:border-cyan-900',
    text: 'text-cyan-700 dark:text-cyan-300',
    accent: 'text-cyan-600 dark:text-cyan-400',
    button: 'bg-cyan-600 hover:bg-cyan-700 text-white',
  },
  red: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-900',
    text: 'text-red-700 dark:text-red-300',
    accent: 'text-red-600 dark:text-red-400',
    button: 'bg-red-600 hover:bg-red-700 text-white',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-950/30',
    border: 'border-purple-200 dark:border-purple-900',
    text: 'text-purple-700 dark:text-purple-300',
    accent: 'text-purple-600 dark:text-purple-400',
    button: 'bg-purple-600 hover:bg-purple-700 text-white',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    border: 'border-orange-200 dark:border-orange-900',
    text: 'text-orange-700 dark:text-orange-300',
    accent: 'text-orange-600 dark:text-orange-400',
    button: 'bg-orange-600 hover:bg-orange-700 text-white',
  }
};

// Helper used by pages to style hero/sections based on series.color
export function getColorClasses(color: string): ColorClasses {
  const key = (color || 'blue').toLowerCase() as SeriesColor;
  return COLOR_MAP[key] ?? COLOR_MAP.blue;
}

// Helper functions
export function getAllManuals(): (Manual & { price: number })[] {
  return seriesConfig.flatMap(series => 
    series.manuals.map(manual => ({
      ...manual,
      price: getManualPrice(manual)
    }))
  )
}

export function getFeaturedManuals(): (Manual & { price: number })[] {
  return getAllManuals().filter(manual => manual.featured)
}

export function getManualBySlug(seriesSlug: string, manualSlug: string): (Manual & { price: number }) | undefined {
  const normalizedSeriesSlug = seriesSlug.trim().toLowerCase();
  const normalizedManualSlug = manualSlug.trim().toLowerCase();
  const series = seriesConfig.find(s => s.slug.trim().toLowerCase() === normalizedSeriesSlug);
  const manual = series?.manuals.find(m => m.slug.trim().toLowerCase() === normalizedManualSlug);
  if (!manual) return undefined;
  return {
    ...manual,
    price: getManualPrice(manual)
  };
}

export function getSeriesBySlug(slug: string): (Series & { bundlePrice: number; bundleSavings: number }) | undefined {
  const normalizedSlug = slug.trim().toLowerCase();
  const series = seriesConfig.find(series => series.slug.trim().toLowerCase() === normalizedSlug);
  if (!series) return undefined;
  return {
    ...series,
    bundlePrice: calculateBundlePrice(series.manuals),
    bundleSavings: calculateBundleSavings(series.manuals)
  };
}

export function getSeriesByCategory(category: string): Series[] {
  return seriesConfig.filter(series => series.category === category)
}

// Categories, learning paths, and pricing tiers
export const categories = {
  markets: {
    name: 'Markets',
    description: 'Market fundamentals, structure, and dynamics',
    icon: '🏪',
    color: 'blue'
  },
  instruments: {
    name: 'Instruments',
    description: 'Financial instruments and product knowledge',
    icon: '🛠️',
    color: 'green'
  },
  analytics: {
    name: 'Analytics',
    description: 'Quantitative methods and analytical techniques',
    icon: '📊',
    color: 'purple'
  },
  risk: {
    name: 'Risk Management',
    description: 'Risk measurement, management, and frameworks',
    icon: '🛡️',
    color: 'red'
  },
  strategy: {
    name: 'Strategy',
    description: 'Investment strategies and portfolio management',
    icon: '♟️',
    color: 'indigo'
  },
  regulation: {
    name: 'Regulation',
    description: 'Regulatory frameworks and compliance',
    icon: '⚖️',
    color: 'gray'
  }
} as const;

export const learningPaths = {
  'derivatives-fundamentals': {
    name: 'Derivatives Fundamentals',
    description: 'Complete derivatives education from basics to advanced strategies',
    series: ['fx-instruments', 'interest-rates'],
    duration: '6-8 weeks',
    difficulty: 'beginner' as const
  },
  'risk-professional': {
    name: 'Risk Management Professional',
    description: 'Comprehensive risk management education for practitioners',
    series: ['risk-management'],
    duration: '8-10 weeks',
    difficulty: 'intermediate' as const
  },
  'treasury-specialist': {
    name: 'Treasury Specialist Track',
    description: 'Complete treasury operations and technology education',
    series: ['money-markets', 'treasury-systems', 'risk-management'],
    duration: '10-12 weeks',
    difficulty: 'intermediate' as const
  }
} as const;

export const pricingTiers = {
  foundation: {
    name: 'Foundation',
    description: 'Essential knowledge for finance professionals',
    priceRange: 'Beginner-friendly',
    color: 'green'
  },
  professional: {
    name: 'Professional',
    description: 'Advanced concepts for experienced practitioners',
    priceRange: 'Intermediate depth',
    color: 'blue'
  },
  expert: {
    name: 'Expert',
    description: 'Specialized knowledge for experts and specialists',
    priceRange: 'Advanced level',
    color: 'purple'
  }
} as const;
